import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import ErrorBoundary from "@/components/ErrorBoundary";

import Home from "@/pages/Home";
import Work from "@/pages/Work";
import ProjectDetail from "@/pages/ProjectDetail";
import AdminLogin from "@/pages/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProfile from "@/pages/admin/AdminProfile";
import AdminProjects from "@/pages/admin/AdminProjects";
import AdminSkills from "@/pages/admin/AdminSkills";
import AdminExperiences from "@/pages/admin/AdminExperiences";
import AdminBrands from "@/pages/admin/AdminBrands";
import AdminCertifications from "@/pages/admin/AdminCertifications";
import AdminServices from "@/pages/admin/AdminServices";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import AdminFAQs from "@/pages/admin/AdminFAQs";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminSubmissions from "@/pages/admin/AdminSubmissions";

function Protected({ children }) {
    const { user, loading } = useAuth();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ink-900 text-ink-200">
                <div className="font-mono text-xs tracking-[0.25em] uppercase">Authenticating…</div>
            </div>
        );
    }
    if (!user) return <Navigate to="/admin/login" replace />;
    return children;
}

export default function App() {
    useEffect(() => {
        api.get("/settings").then((res) => {
            const settings = res.data;
            if (settings?.favicon_url) {
                let link = document.querySelector("link[rel~='icon']");
                if (!link) {
                    link = document.createElement("link");
                    link.rel = "icon";
                    document.getElementsByTagName("head")[0].appendChild(link);
                }
                link.href = settings.favicon_url;
            }
        }).catch(() => {});
    }, []);

    return (
        <AuthProvider>
            <BrowserRouter>
                <ErrorBoundary>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/work" element={<Work />} />
                        <Route path="/projects/:slug" element={<ProjectDetail />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route
                            path="/admin"
                            element={
                                <Protected>
                                    <AdminLayout />
                                </Protected>
                            }
                        >
                            <Route index element={<AdminDashboard />} />
                            <Route path="profile" element={<AdminProfile />} />
                            <Route path="projects" element={<AdminProjects />} />
                            <Route path="skills" element={<AdminSkills />} />
                            <Route path="experiences" element={<AdminExperiences />} />
                            <Route path="brands" element={<AdminBrands />} />
                            <Route path="certifications" element={<AdminCertifications />} />
                            <Route path="services" element={<AdminServices />} />
                            <Route path="testimonials" element={<AdminTestimonials />} />
                            <Route path="faqs" element={<AdminFAQs />} />
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="submissions" element={<AdminSubmissions />} />
                        </Route>
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </ErrorBoundary>
                <Toaster theme="dark" position="bottom-right" richColors />
            </BrowserRouter>
        </AuthProvider>
    );
}
